import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { colors, Typography, Tooltip } from '@sweep-io/sweep-design';
import { Globe, Users } from '@sweep-io/sweep-design/dist/icons';
import { TruncatedTextTooltip } from '../TruncatedTextTooltip';
import { ArtifactIllustration } from './ArtifactIllustrations';
import { ArtifactData } from '../ai-chat/aiChatTypes';
import { useArtifacts, simulateArtifactStreaming } from './useArtifacts';
import {
  getContentType,
  getDemoArtifactContent,
  isDemoArtifact,
} from '../../helpers/artifactHelpers';
import { useAiAgentApiFacade } from '../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../telemetry';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useSelector } from 'react-redux';
import { selectChatsDetails, selectActiveChatId } from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';
import { getArtifactParts } from '../ai-chat/aiChatHelpers';
import { ARTIFACT_PREVIEW_NARROW_QUERY } from '../../aiAgentCenter/aiAgentsConsts';

interface ArtifactPreviewProps {
  artifactData: {
    artifactId: string;
    title: string;
    versionId?: string; // For historical artifacts from chat history
    version?: number; // Version number for subtitle display
  };
}

export const ArtifactPreview: React.FC<ArtifactPreviewProps> = ({ artifactData }) => {
  const {
    openExistingArtifact,
    startArtifactStreaming,
    addArtifactData,
    addStreamingContent,
    completeArtifactStreaming,
    artifacts,
    streamingContent,
    isStreamingComplete,
    currentArtifactId,
    setStreamingComplete,
  } = useArtifacts();

  const { get_artifact } = useAiAgentApiFacade();
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const fetchingRef = useRef<Set<string>>(new Set());
  const { dreamforceDemo, artifactsPage: isArtifactsPageEnabled } = useFeatureToggle();

  // Get sharing status from context (updated when popover changes it)
  const artifactInContext = artifacts[artifactData.artifactId];
  const sharingStatus =
    artifactInContext?.isShared !== undefined || artifactInContext?.isPublic !== undefined
      ? {
          isShared: artifactInContext.isShared ?? false,
          isPublic: artifactInContext.isPublic ?? false,
        }
      : null;

  // Get chat messages to find all versions
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const chatsDetails = useSelector(selectChatsDetails);
  const currentChat = activeChatId && chatsDetails ? chatsDetails[activeChatId]?.data : null;

  const openOrBuildArtifact = useCallback(async () => {
    const existingArtifact = artifacts[artifactData.artifactId];

    if (existingArtifact) {
      // Artifact already exists - open the panel
      openExistingArtifact(artifactData.artifactId, artifactData.versionId);
      return;
    }

    // Check if this is a historical artifact (has versionId)
    if (artifactData.versionId) {
      // Prevent duplicate fetches
      if (fetchingRef.current.has(artifactData.versionId)) {
        return;
      }

      fetchingRef.current.add(artifactData.versionId);

      // Historical artifact from chat history - fetch from server
      setIsLoadingHistorical(true);
      try {
        // Step 1: Find all versions of this artifact in chat history
        const allVersionRefs: Array<{ versionId: string; title: string }> = [];

        if (currentChat?.aiChatDetails?.messages) {
          currentChat.aiChatDetails.messages.forEach((message) => {
            const artifactParts = getArtifactParts(message.content);
            artifactParts.forEach((part) => {
              if (part.artifactId === artifactData.artifactId) {
                allVersionRefs.push({
                  versionId: part.versionId,
                  title: part.title,
                });
              }
            });

            if (
              message.artifactData &&
              message.artifactData.artifactId === artifactData.artifactId &&
              message.artifactData.versionId
            ) {
              // Check if we haven't already added this version
              if (
                !allVersionRefs.some((ref) => ref.versionId === message.artifactData!.versionId)
              ) {
                allVersionRefs.push({
                  versionId: message.artifactData.versionId,
                  title: message.artifactData.title,
                });
              }
            }
          });
        }

        // Step 2: Fetch only the clicked version initially
        const fetchedArtifact = await get_artifact(artifactData.versionId);

        // Detect content type from the code
        const detectedContentType = getContentType(fetchedArtifact.code);

        // Step 3: Create versions array with fetched data for clicked version,
        // and placeholders for other versions (to be lazy loaded)
        const versions = allVersionRefs.map((ref) => {
          if (ref.versionId === fetchedArtifact.id) {
            // This is the version we just fetched - include full data
            return {
              versionId: fetchedArtifact.id,
              title: ref.title.replace(/^"|"$/g, ''), // Remove surrounding quotes
              content: fetchedArtifact.code,
              contentType: detectedContentType,
              createdAt: fetchedArtifact.createdAt,
              version: fetchedArtifact.version,
            };
          } else {
            // Placeholder for other versions - will be lazy loaded when selected
            return {
              versionId: ref.versionId,
              title: ref.title.replace(/^"|"$/g, ''),
              content: '', // Empty - will be fetched when needed
              contentType: detectedContentType, // Assume same type for now
              createdAt: '', // Will be filled when fetched
              version: undefined, // Will be filled when fetched
            };
          }
        });

        // Sort versions by the order they appear in chat (earlier = lower version)
        const sortedVersions = versions.map((v, index) => ({
          ...v,
          version: v.version ?? index + 1, // Assign version numbers based on order
        }));

        // Add to artifacts store
        const artifactDataToStore: ArtifactData = {
          artifactId: artifactData.artifactId,
          versions: sortedVersions,
          currentVersion: fetchedArtifact.id,
          isShared: fetchedArtifact.isShared,
          isPublic: fetchedArtifact.isPublic,
        };

        addArtifactData(artifactData.artifactId, artifactDataToStore);
        openExistingArtifact(artifactData.artifactId, artifactData.versionId);
      } catch (error) {
        telemetry.captureError(error);
      } finally {
        setIsLoadingHistorical(false);
        fetchingRef.current.delete(artifactData.versionId!);
      }
      return;
    }

    // For demo/mock artifacts only (requires dreamforceDemo feature flag)
    if (dreamforceDemo && isDemoArtifact(artifactData.artifactId)) {
      // Demo artifact - use simulation
      startArtifactStreaming(artifactData.artifactId);

      // Get the demo content based on artifact ID
      const artifactContent = getDemoArtifactContent(artifactData.artifactId);

      // Start the streaming simulation
      simulateArtifactStreaming(
        artifactData.artifactId,
        artifactContent,
        addStreamingContent, // Accumulate chunks
        () => setStreamingComplete(true),
      );
    }
  }, [
    artifactData,
    artifacts,
    dreamforceDemo,
    openExistingArtifact,
    currentChat?.aiChatDetails?.messages,
    get_artifact,
    addArtifactData,
    startArtifactStreaming,
    addStreamingContent,
    setStreamingComplete,
  ]);

  useEffect(() => {
    if (isStreamingComplete && streamingContent && currentArtifactId === artifactData.artifactId) {
      const detectedContentType = getContentType(streamingContent);

      // Use the ACTUAL accumulated streaming content
      const finalArtifactData: ArtifactData = {
        artifactId: artifactData.artifactId,
        versions: [
          {
            versionId: '1',
            title: artifactData.title,
            content: streamingContent,
            contentType: detectedContentType,
            createdAt: new Date().toISOString(),
            version: 1, // Demo artifacts are always version 1
          },
        ],
        currentVersion: '1',
      };

      addArtifactData(artifactData.artifactId, finalArtifactData);
      completeArtifactStreaming();
    }
  }, [
    isStreamingComplete,
    streamingContent,
    currentArtifactId,
    artifactData.artifactId,
    artifactData.title,
    addArtifactData,
    completeArtifactStreaming,
  ]);

  // Auto-open panel when artifact message appears (only for new artifacts that don't exist yet)
  // For real artifacts, useAgentsChats handles the opening automatically
  useEffect(() => {
    const existingArtifact = artifacts[artifactData.artifactId];

    // Only auto-open for demo artifacts that don't exist yet (requires dreamforceDemo flag)
    if (dreamforceDemo && !existingArtifact && isDemoArtifact(artifactData.artifactId)) {
      openOrBuildArtifact();
    }
  }, [openOrBuildArtifact, artifacts, artifactData.artifactId, dreamforceDemo]);

  const handleViewDetails = () => {
    openOrBuildArtifact();
  };

  return (
    <Box
      sx={{
        containerType: 'inline-size',
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '12px',
        padding: 2,
        backgroundColor: 'white',
        width: '100%',
        height: '78px',
        transition: 'all 0.2s ease-in-out',
        cursor: isLoadingHistorical ? 'wait' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: colors.blue[500],
        },
      }}
      onClick={handleViewDetails}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          paddingRight: '285px',
          [ARTIFACT_PREVIEW_NARROW_QUERY]: {
            paddingRight: 0,
          },
        }}
      >
        <TruncatedTextTooltip variant="body">{artifactData.title}</TruncatedTextTooltip>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" color={colors.grey[700]}>
            Interactive artifact
          </Typography>
          {isArtifactsPageEnabled &&
            sharingStatus &&
            (sharingStatus.isShared || sharingStatus.isPublic) && (
              <>
                <Typography variant="caption" color={colors.grey[500]}>
                  |
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {sharingStatus.isShared && (
                    <Tooltip title="Shared with your company" placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Users size="medium" color={colors.grey[700]} />
                      </Box>
                    </Tooltip>
                  )}
                  {sharingStatus.isPublic && (
                    <Tooltip title="Published: Public link enabled" placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Globe size="medium" color={colors.grey[700]} />
                      </Box>
                    </Tooltip>
                  )}
                </Stack>
              </>
            )}
        </Stack>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: '18px',
          bottom: '-7px',
          [ARTIFACT_PREVIEW_NARROW_QUERY]: {
            display: 'none',
          },
        }}
      >
        <ArtifactIllustration />
      </Box>
    </Box>
  );
};
